import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { useMeetingLock } from '../contexts/MeetingLockContext';

// Check for Web Speech API support
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const hasSpeechRecognition = !!SpeechRecognition;

function VoiceTextInput({ 
  value, 
  onChange, 
  onBlur,
  placeholder = 'Type or use microphone...', 
  rows = 3,
  className = '',
  testId = 'voice-input'
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const { active: meetingLocked } = useMeetingLock();
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch (e) {}
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  // Primary: Browser native Speech Recognition (works great on mobile)
  const startBrowserRecognition = () => {
    if (meetingLocked) {
      toast.warning('Microphone is locked while a meeting is in progress.  Take your After selfie in the Meeting Log to release the lock.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    let finalTranscript = '';
    
    recognition.onresult = (event) => {
      let interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript = transcript;
        }
      }
      
      // Update text field with accumulated final + current interim
      const currentBase = value || '';
      const separator = currentBase && !currentBase.endsWith(' ') ? ' ' : '';
      const displayText = currentBase + separator + finalTranscript + interimTranscript;
      onChange(displayText.trimEnd());
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        toast.error('Microphone permission denied. Please allow microphone access.');
      } else if (event.error !== 'aborted') {
        toast.error('Voice recognition error. Try again.');
      }
      setIsRecording(false);
    };

    recognition.onend = () => {
      // Finalize the text
      if (finalTranscript.trim()) {
        const currentBase = value || '';
        const separator = currentBase && !currentBase.endsWith(' ') ? ' ' : '';
        onChange((currentBase + separator + finalTranscript).trimEnd());
      }
      setIsRecording(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
    toast.info('Listening... Speak now', { duration: 2000 });
  };

  const stopBrowserRecognition = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsRecording(false);
  };

  // Fallback: Record audio and send to backend Whisper API
  const startBackendRecording = async () => {
    if (meetingLocked) {
      toast.warning('Microphone is locked while a meeting is in progress.  Take your After selfie in the Meeting Log to release the lock.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeViaBackend(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info('Recording... Tap mic again to stop', { duration: 2000 });
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopBackendRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const transcribeViaBackend = async (audioBlob) => {
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/transcribe`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (data.text) {
          const newValue = value ? `${value} ${data.text}` : data.text;
          onChange(newValue);
          toast.success('Voice transcribed successfully');
        }
      } else {
        toast.error('Transcription failed. Please try typing instead.');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error('Could not transcribe audio. Please try typing instead.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleMicClick = () => {
    if (isRecording) {
      // Stop recording
      if (hasSpeechRecognition) {
        stopBrowserRecognition();
      } else {
        stopBackendRecording();
      }
    } else {
      // Start recording - prefer browser native for instant results on mobile
      if (hasSpeechRecognition) {
        startBrowserRecognition();
      } else {
        startBackendRecording();
      }
    }
  };

  return (
    <div className="relative" data-testid={`${testId}-wrapper`}>
      <Textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        rows={rows}
        className={`pr-12 ${className}`}
        data-testid={testId}
      />
      <button
        type="button"
        onClick={handleMicClick}
        disabled={isTranscribing}
        className={`absolute right-2 top-2 p-2 rounded-full transition-colors ${
          isRecording 
            ? 'bg-red-500 text-white animate-pulse' 
            : isTranscribing
              ? 'bg-gray-200 text-gray-400'
              : 'bg-blue-100 text-blue-600 hover:bg-blue-200 active:bg-blue-300'
        }`}
        data-testid={`${testId}-mic`}
        title={isRecording ? 'Stop recording' : 'Start voice input'}
      >
        {isTranscribing ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : isRecording ? (
          <MicOff className="w-5 h-5" />
        ) : (
          <Mic className="w-5 h-5" />
        )}
      </button>
    </div>
  );
}

export default VoiceTextInput;
